import { useState } from "react";
import { Form } from "@/components/shared/forms/Form";
import { useRouteContext } from "@tanstack/react-router";
import { useForm, SubmitHandler } from "react-hook-form";
import { FiEye, FiPlus, FiSave, FiTrash2 } from "react-icons/fi";

import { type SCLR, SCLR_TEMPLATE } from "@/models";
import { sclrMutationFunctions } from "@/models/stff";
import { useLeaData } from "@/queries.ts";
import Popup from "@/components/shared/Popup";
import SCLRAddEditForm from "@/components/records/staff/SCLRAddEditForm";
import { type ComboBoxOption } from "@/components/shared/forms/SearchComboBox";
import { useRecordSubmission } from "@/hooks/useRecordSubmission";
import LoaderButton from "@/components/shared/LoaderButton";
import { DeleteConfirmModal } from "@/components/shared/DeleteConfirmModal";

export default function SCLREditDialog({
  record,
  defaults,
  onClose,
  onDeleted,
}: {
  record?: SCLR;
  defaults?: Partial<SCLR>;
  onClose: () => void;
  onDeleted?: (record: SCLR) => void;
  viewOnly?: boolean;
}) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const result = useLeaData(leaContext.currentUserOrg.esiIdPath);
  const leaData = result.data;

  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const form = useForm<SCLR>({
    defaultValues: record ?? { ...SCLR_TEMPLATE, ...(defaults || {}) },
  });

  const isEdit = !!record;

  const watchPreschoolCode = form.watch("PreschoolCode");
  const watchProgramYear = form.watch("ProgramYear");

  const classroomOptions: ComboBoxOption[] =
    leaData?.entities?.classrooms && watchPreschoolCode
      ? Object.values(leaData.entities.classrooms)
          .map((c) => c.records[0])
          .filter(
            (clrm) =>
              clrm &&
              clrm.LocalClassroomId &&
              clrm.PreschoolCode === watchPreschoolCode &&
              clrm.ProgramYear === watchProgramYear &&
              !clrm.EffectiveEndDate
          )
          .map((clrm) => ({
            value: clrm.LocalClassroomId,
            label: clrm.ClassroomName ?? "",
            sublabel: `ID: ${clrm.LocalClassroomId}`,
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
      : [];

  const { isSubmitting, setSubmitResult, handleSubmitStart } =
    useRecordSubmission({
      form,
      recordType: "SCLR",
      toastMessage:
        "Staff classroom record submitted.\nWaiting for validation...",
      onSuccess: onClose,
    });

  const {
    isSubmitting: isDeleteSubmitting,
    setSubmitResult: setDeleteSubmitResult,
    handleSubmitStart: handleDeleteStart,
  } = useRecordSubmission({
    form,
    recordType: "SCLR",
    toastMessage: "Staff classroom record submitted for deletion",
    successMessage: "Staff classroom record deleted successfully",
    onSuccess: () => {
      if (record && onDeleted) onDeleted(record);
      onClose();
    },
  });

  const mutateFns = sclrMutationFunctions(leaData, setSubmitResult);
  const deleteMutateFns = sclrMutationFunctions(leaData, setDeleteSubmitResult);

  const onSubmit: SubmitHandler<SCLR> = (x) => {
    handleSubmitStart();
    mutateFns.handleSave(x, false);
  };

  const handleConfirmDelete = () => {
    setShowDeleteConfirm(false);
    if (record) {
      setIsDeleting(true);
      handleDeleteStart();
      deleteMutateFns.handleSave(record, true);
    }
  };

  const canEdit = leaContext.hasRole("SCLREdit");
  const formId = "frmSCLREditDialog";

  return (
    <>
      <Popup
        show={true}
        enforceFocus={!showDeleteConfirm}
        header={
          isEdit ? (
            <div
              className="d-flex align-items-center gap-2"
              style={{ fontSize: "1.25rem" }}
            >
              <FiEye size={20} />
              <span>View Staff Classroom Record</span>
            </div>
          ) : (
            <div
              className="d-flex align-items-center gap-2"
              style={{ fontSize: "1.25rem" }}
            >
              {canEdit ? <FiPlus size={20} /> : <FiEye size={20} />}
              <span>
                {canEdit
                  ? "Add Staff Classroom Record"
                  : "View Staff Classroom Record"}
              </span>
            </div>
          )
        }
        hasButton={false}
        submitFormId={canEdit && !isEdit ? formId : undefined}
        submitButtonText={
          <span className="d-flex align-items-center">
            <FiSave className="me-1" />
            Save
          </span>
        }
        submitButtonLoading={isSubmitting}
        submitButtonLoadingText="Submitting..."
        secondButtonText={isEdit ? "Close" : "Cancel"}
        size="lg"
        onClose={onClose}
        onSecondButtonClick={onClose}
        canEdit={canEdit && !isEdit}
        leftFooterButton={
          canEdit && isEdit ? (
            <LoaderButton
              variant="danger"
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              loading={isDeleting && isDeleteSubmitting}
              loadingText="Deleting..."
              className="d-flex align-items-center"
            >
              <FiTrash2 className="me-1" />
              Delete
            </LoaderButton>
          ) : undefined
        }
      >
        <Form id={formId} onSubmit={form.handleSubmit(onSubmit)}>
          <SCLRAddEditForm
            staffClassroom={record}
            form={form}
            sclrDefaults={defaults}
            classroomOptions={classroomOptions}
          />
        </Form>
      </Popup>

      {showDeleteConfirm && (
        <DeleteConfirmModal
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </>
  );
}
